import {ProjectSchemaDisplay, SCHEMA_CODE} from "../project_schemas";
import {BetaReaderMatchUse, ProjectDescriptorType, ProjectDescriptorUse} from "../enums";

export const FICTION_SERIES: ProjectSchemaDisplay = {
    displayName: 'Fiction Book and/or Series - Use this for general novel writing, short stories, etc.',
    code: 'FICTION_SERIES',

    schema: {
        sectionLayout: [
            {
                level: 0,
                sectionName: 'Series',
                toolbarSelectedName: 'Split Series',
                toolbarDropDownLabel: 'Split this document into separate <strong>series</strong> at the cursor when I press the <i class="scrvi scrvi-books"></i> button',
                toolbarIconHTML: '<i class="scrvi scrvi-books"></i>',
                tocIconHTML: '<i class="scrvi scrvi-books"></i>',
                latexCode: 'SERIES',
                requiresName: true,
                hasContent: false,
                metadataKeys: []
            },
            {
                level: 1,
                sectionName: 'Book',
                toolbarSelectedName: 'Split Book',
                toolbarDropDownLabel: 'Split this document into separate <strong>books</strong> at the cursor when I press the <i class="scrvi scrvi-book-add"></i> button',
                toolbarIconHTML: '<i class="scrvi scrvi-book-add"></i>',
                tocIconHTML: '<i class="scrvi scrvi-book-add"></i>',
                latexCode: 'BOOK',
                requiresName: true,
                hasContent: true,
                metadataKeys: []
            },
            {
                level: 2,
                sectionName: 'Section/Act',
                toolbarSelectedName: 'New Section/Act',
                toolbarDropDownLabel: 'Start a new <strong>section</strong> or <strong>act</strong> at the cursor when I press the <i class="pscrvi scrvi-section"></i> button',
                toolbarIconHTML: '<i class="scrvi scrvi-section"></i>',
                tocIconHTML: '<i class="scrvi scrvi-section"></i>',
                latexCode: 'SECTION',
                requiresName: true,
                hasContent: true,
                metadataKeys: []
            },
            {
                level: 3,
                sectionName: 'Chapter',
                toolbarSelectedName: 'New Chapter',
                toolbarDropDownLabel: 'Start a new <strong>chapter</strong> at the cursor when I press the <i class="scrvi scrvi-chapter-add"></i> button',
                toolbarIconHTML: '<i class="scrvi scrvi-chapter-add"></i>',
                tocIconHTML: '<i class="scrvi scrvi-chapter-add"></i>',
                latexCode: 'CHAPTER',
                requiresName: true,
                hasContent: true,
                metadataKeys: []
            },
            {
                level: 4,
                sectionName: 'Scene',
                toolbarSelectedName: 'New Scene',
                toolbarDropDownLabel: 'Start a new <strong>scene</strong> at the cursor when I press the <i class="pi pi-hashtag"></i> button',
                toolbarIconHTML: '<i class="pi pi-hashtag"></i>',
                tocIconHTML: '<i class="pi pi-hashtag"></i>',
                latexCode: 'SCENE',
                requiresName: false,
                hasContent: true,
                metadataKeys: []
            },
            {
                level: 5,
                sectionName: 'Note',
                toolbarSelectedName: 'New Note',
                toolbarDropDownLabel: 'Create a <strong>note</strong> at the cursor when I press the <i class="pi pi-pencil"></i> button',
                toolbarIconHTML: '<i class="pi pi-pencil"></i>',
                tocIconHTML: '<i class="pi pi-pencil"></i>',
                latexCode: 'NOTE',
                requiresName: false,
                hasContent: true,
                metadataKeys: []
            }
        ],
        projectLevel: 1,  //Array index of what type this project actually "is"
    },
    initialContentHTML: "<strong>Welcome to Scrivendium's writing area.</strong> Please replace this text with your creation.",
    initialContent: "Welcome to Scrivendium's writing area. Please replace this text with your creation.",
    projectDescriptorCategories: [
        {
            uniqueId: 'BIO_INFO',
            betaReaderMatchWeight: null,
            betaReaderMatchUse: BetaReaderMatchUse.DO_NOT_USE_TO_MATCH,
            uses: [
                ProjectDescriptorUse.BETA_READER_PROFILE,
                ProjectDescriptorUse.WRITER_PROJECT,
                ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT
            ],
            variants: [
                {
                    label: "Bio Info",
                    help: "",
                    uses: [
                        ProjectDescriptorUse.BETA_READER_PROFILE,
                        ProjectDescriptorUse.WRITER_PROJECT,
                        ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT
                    ],
                    questionType: ProjectDescriptorType.LONGTEXT,
                    tagIcon: "pi-box",
                    tagColor: null
                }
            ],
            descriptors: [
                {
                    uniqueId: "ABOUT_ME",
                    label: "About Me",
                    help: null
                }
            ]
        },
        {
            uniqueId: 'GENRE_PREFERENCE',
            betaReaderMatchWeight: 1,
            betaReaderMatchUse: BetaReaderMatchUse.GOOD_SCORE_ON_MATCH,
            uses: [
                ProjectDescriptorUse.BETA_READER_PROFILE,
                ProjectDescriptorUse.WRITER_PROJECT,
                ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT,
                ProjectDescriptorUse.WRITER_VIEW_OF_BETA_READER_PROFILE
            ],
            variants: [
                {
                    label: "I like to read books in these genres (check all that apply)",
                    help: "If you’re unsure about whether or not to check a box, err on the side of including the genre. You don’t have to match with any of these genres, it just gives you the option.",
                    uses: [
                        ProjectDescriptorUse.BETA_READER_PROFILE,
                        ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT
                    ],
                    questionType: ProjectDescriptorType.CHECKBOXES,
                    tagIcon: null,
                    tagColor: null
                },
                {
                    label: "Genre",
                    help: "What genres best describe your book? ",
                    uses: [
                        ProjectDescriptorUse.WRITER_PROJECT,
                        ProjectDescriptorUse.WRITER_VIEW_OF_BETA_READER_PROFILE
                    ],
                    questionType: ProjectDescriptorType.CHECKBOXES,
                    tagIcon: null,
                    tagColor: null
                }
            ],
            descriptors: [
                {
                    uniqueId: "HISTORICAL_FICTION",
                    label: "Historical Fiction",
                    help: null
                },
                {
                    uniqueId: "FANTASY",
                    label: "Fantasy",
                    help: null
                },
                {
                    uniqueId: "ACTION",
                    label: "Action",
                    help: null
                },
                {
                    uniqueId: "CREATIVE_NONFICTION",
                    label: "Creative Nonfiction",
                    help: null
                },
                {
                    uniqueId: "ADVENTURE",
                    label: "Adventure",
                    help: ""
                },
                {
                    uniqueId: "SUPERHERO",
                    label: "Superhero",
                    help: null
                },
                {
                    uniqueId: "NONFICTION",
                    label: "Nonfiction",
                    help: null
                },
                {
                    uniqueId: "REALISTIC_FICTION",
                    label: "Realistic Fiction",
                    help: null
                },
                {
                    uniqueId: "LITERARY_FICTION",
                    label: "Literary Fiction",
                    help: null
                },
                {
                    uniqueId: "THRILLER",
                    label: "Thriller",
                    help: null
                },
                {
                    uniqueId: "ROMANCE_18P",
                    label: "Romance (Lots of spice; 18+)",
                    help: ""
                },
                {
                    uniqueId: "SCIENCE_FICTION",
                    label: "Science Fiction",
                    help: null
                },
                {
                    uniqueId: "STEAMPUNK",
                    label: "Steampunk",
                    help: null
                },
                {
                    uniqueId: "HORROR",
                    label: "Horror",
                    help: null
                },
                {
                    uniqueId: "CRIME_MYSTERY",
                    label: "Crime/Mystery",
                    help: null
                },
                {
                    uniqueId: "EROTICA",
                    label: "Erotica (18+)",
                    help: null
                },
                {
                    uniqueId: "ROMANCE_0",
                    label: "Romance (No Spice)",
                    help: null
                },
                {
                    uniqueId: "WESTERN",
                    label: "Western",
                    help: null
                },
                {
                    uniqueId: "ROMANCE_1",
                    label: "Romance (Some Spice)",
                    help: null
                }
            ]
        },
        {
            uniqueId: "LENGTH",
            betaReaderMatchWeight: 5,
            betaReaderMatchUse: BetaReaderMatchUse.GOOD_SCORE_ON_MATCH,
            uses: [
                ProjectDescriptorUse.BETA_READER_PROFILE,
                ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT,
                ProjectDescriptorUse.WRITER_PROJECT
            ],
            variants: [
                {
                    label: "Length (in Words)",
                    help: "How long is your book? (If you work in pages rather than words, one page is roughly 250 words)",
                    uses: [
                        ProjectDescriptorUse.WRITER_PROJECT,
                        ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT
                    ],
                    questionType: ProjectDescriptorType.RADIO_BUTTONS,
                    tagIcon: "pi-book",
                    tagColor: null
                },
                {
                    label: "Preferred reading length",
                    help: "What length of stories would you like to read?",
                    uses: [
                        ProjectDescriptorUse.BETA_READER_PROFILE
                    ],
                    questionType: ProjectDescriptorType.CHECKBOXES,
                    tagIcon: "pi-book",
                    tagColor: null
                }
            ],
            descriptors: [
                {
                    uniqueId: "U3K",
                    label: "Under 3000 words",
                    help: null
                },
                {
                    uniqueId: "3K_10K",
                    label: "3,001 - 9,999",
                    help: null
                },
                {
                    uniqueId: "10K_30K",
                    label: "10,000 - 29,999",
                    help: null
                },
                {
                    uniqueId: "30K_50K",
                    label: "30,000 - 49,999",
                    help: null
                },
                {
                    uniqueId: "50K_80K",
                    label: "50,000 - 79,999",
                    help: null
                },
                {
                    uniqueId: "80K_120K",
                    label: "80,000 - 119,999",
                    help: null
                },
                {
                    uniqueId: "120K_200K",
                    label: "120,000 - 199,999",
                    help: null
                },
                {
                    uniqueId: "200KP",
                    label: "200,000+ ",
                    help: null
                }
            ]
        },
        {
            uniqueId: "AGE",
            betaReaderMatchWeight: 1,
            betaReaderMatchUse: BetaReaderMatchUse.GOOD_SCORE_ON_MATCH,
            uses: [
                ProjectDescriptorUse.BETA_READER_PROFILE,
                ProjectDescriptorUse.WRITER_PROJECT,
                ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT
            ],
            variants: [
                {
                    label: "What's the target age range for your story?",
                    help: "When in doubt, select the age of your primary protagonist",
                    uses: [
                        ProjectDescriptorUse.WRITER_PROJECT,
                        ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT
                    ],
                    questionType: ProjectDescriptorType.RADIO_BUTTONS,
                    tagIcon: "pi-align-center",
                    tagColor: null
                },
                {
                    label: "What age ranges do you prefer to read?",
                    help: "Note: This isn't always your age range - plenty of adults like reading middle grade books!",
                    uses: [
                        ProjectDescriptorUse.BETA_READER_PROFILE
                    ],
                    questionType: ProjectDescriptorType.CHECKBOXES,
                    tagIcon: "pi-align-justify",
                    tagColor: null
                }
            ],
            descriptors: [
                {
                    uniqueId: "CHILD",
                    label: "Childrens and Chapter Books (3 - 10 yrs)",
                    help: null
                },
                {
                    uniqueId: "MIDDLE_GRADE",
                    label: "Middle Grade (8 - 12 yrs)",
                    help: null
                },
                {
                    uniqueId: "YA",
                    label: "Young Adult (12 - 18 yrs)",
                    help: null
                },
                {
                    uniqueId: "NEW_ADULT",
                    label: "New Adult (17 - 25 yrs)",
                    help: "The difference between New Adult and Adult is very fine. If the book concerns themes of young adulthood - college, finding careers, and generally making their place in society - it likely falls in this category."
                },
                {
                    uniqueId: "ADULT",
                    label: "Adult (18+)",
                    help: ""
                }
            ]
        },
        {
            uniqueId: "TRIGGER_WARNINGS",
            betaReaderMatchWeight: 1,
            betaReaderMatchUse: BetaReaderMatchUse.MUST_NOT_MATCH_ANY,
            uses: [
                ProjectDescriptorUse.BETA_READER_PROFILE,
                ProjectDescriptorUse.WRITER_PROJECT
            ],
            variants: [
                {
                    label: "Trigger Warnings",
                    help: "Please select all topics that your book contains. When in doubt, include it!",
                    uses: [
                        ProjectDescriptorUse.WRITER_PROJECT,
                        ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT
                    ],
                    questionType: ProjectDescriptorType.CHECKBOXES,
                    tagIcon: null,
                    tagColor: null
                },
                {
                    label: "Please don’t show me books with these topics (check all that apply)",
                    help: "Checking these boxes will keep books that feature these topics from being shown to you.",
                    uses: [
                        ProjectDescriptorUse.BETA_READER_PROFILE
                    ],
                    questionType: ProjectDescriptorType.CHECKBOXES,
                    tagIcon: null,
                    tagColor: null
                }
            ],
            descriptors: [
                {
                    uniqueId: "ADOPTION",
                    label: "Adoption",
                    help: null
                },
                {
                    uniqueId: "ABLEISM",
                    label: "Ableism",
                    help: null
                },
                {
                    uniqueId: "ABORTION",
                    label: "Abortion",
                    help: null
                },
                {
                    uniqueId: "CONFINEMENT",
                    label: "Confinement",
                    help: null
                },
                {
                    uniqueId: "ANIMAL_CRUELTY",
                    label: "Animal Cruelty",
                    help: null
                },
                {
                    uniqueId: "BODILY_FLUIDS",
                    label: "Blood/bodily fluids/gore",
                    help: null
                },
                {
                    uniqueId: "CLASSISM",
                    label: "Classism",
                    help: null
                },
                {
                    uniqueId: "DEPRESSION",
                    label: "Depression, Mental Health Issues, or Suicidal Ideation",
                    help: null
                },
                {
                    uniqueId: "DIVORCE",
                    label: "Divorce",
                    help: null
                },
                {
                    uniqueId: "EPIDEMICS",
                    label: "Epidemics",
                    help: null
                },
                {
                    uniqueId: "HATE_CRIMES",
                    label: "Hate Crimes",
                    help: null
                },
                {
                    uniqueId: "HOMOPHOBIA",
                    label: "Homophobia",
                    help: null
                },
                {
                    uniqueId: "BULLYING",
                    label: "Bullying",
                    help: null
                },
                {
                    uniqueId: "DEATH",
                    label: "Death",
                    help: null
                },
                {
                    uniqueId: "GRAPHIC_SEX",
                    label: "Graphic Sex",
                    help: null
                },
                {
                    uniqueId: "SELF_HARM",
                    label: "Self-Harm",
                    help: null
                },
                {
                    uniqueId: "SEXUAL_VIOLENCE",
                    label: "Sexual Violence",
                    help: null
                },
                {
                    uniqueId: "NUDITY",
                    label: "Nudity",
                    help: null
                },
                {
                    uniqueId: "ADDICTION",
                    label: "Addiction",
                    help: null
                },
                {
                    uniqueId: "GRAPHIC_VIOLENCE",
                    label: "Graphic Violence or Death",
                    help: null
                },
                {
                    uniqueId: "GENOCIDE",
                    label: "Genocide",
                    help: null
                },
                {
                    uniqueId: "ABUSE",
                    label: "Abuse",
                    help: null
                },
                {
                    uniqueId: "DISCRIMINATION",
                    label: "Discrimination",
                    help: null
                },
                {
                    uniqueId: "HUMAN_TRAFFICKING",
                    label: "Human Trafficking",
                    help: null
                },
                {
                    uniqueId: "WAR",
                    label: "War",
                    help: null
                },
                {
                    uniqueId: "MASS_SHOOTINGS",
                    label: "Mass Shootings",
                    help: null
                }
            ]
        },
        {
            uniqueId: "NOTES",
            betaReaderMatchWeight: null,
            betaReaderMatchUse: BetaReaderMatchUse.DO_NOT_USE_TO_MATCH,
            uses: [
                ProjectDescriptorUse.WRITER_PROJECT,
                ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT
            ],
            variants: [
                {
                    label: "Notes",
                    help: "Is there anything else you'd like to say?",
                    uses: [
                        ProjectDescriptorUse.WRITER_PROJECT
                    ],
                    questionType: ProjectDescriptorType.LONGTEXT,
                    tagIcon: null,
                    tagColor: null
                },
                {
                    label: "Notes",
                    help: "These are additional bits of info the writer wants you to have",
                    uses: [
                        ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT
                    ],
                    questionType: ProjectDescriptorType.TEXT,
                    tagIcon: null,
                    tagColor: null
                }
            ],
            descriptors: [
                {
                    uniqueId: "NOTES",
                    label: "Notes",
                    help: "Anything else you want to share?"
                }
            ]
        },
        {
            uniqueId: "FEEDBACK_REQ",
            betaReaderMatchWeight: null,
            betaReaderMatchUse: BetaReaderMatchUse.DO_NOT_USE_TO_MATCH,
            uses: [
                ProjectDescriptorUse.WRITER_PROJECT,
                ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT,
                ProjectDescriptorUse.BETA_READER_PROFILE,
                ProjectDescriptorUse.WRITER_VIEW_OF_BETA_READER_PROFILE
            ],
            variants: [
                {
                    label: "What kind of feedback do you need?",
                    help: "Ex: Plot, sensitivity reading, tone, etc.",
                    uses: [
                        ProjectDescriptorUse.WRITER_PROJECT,
                        ProjectDescriptorUse.BETA_READER_VIEW_OF_PROJECT
                    ],
                    questionType: ProjectDescriptorType.LONGTEXT,
                    tagIcon: null,
                    tagColor: null
                },
                {
                    label: "What kind of feedback are you prepared to provide?",
                    help: "When in doubt, you can describe the things you like looking for in novels. If you're fantastic at spotting typos, say that! If you're a harsh critiquer, say that too.",
                    uses: [
                        ProjectDescriptorUse.BETA_READER_PROFILE,
                        ProjectDescriptorUse.WRITER_VIEW_OF_BETA_READER_PROFILE
                    ],
                    questionType: ProjectDescriptorType.LONGTEXT,
                    tagIcon: null,
                    tagColor: null
                }
            ],
            descriptors: [
                {
                    uniqueId: "FEEDBACK",
                    label: "",
                    help: ""
                }
            ]
        }
    ]

}
